import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Select, Button, Card, Collapse, message, Spin, Steps, Row, Col, Upload, Radio } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import Dashboard from "../api/dashboard";
import CustomHeader from "../container/Header";
import PageDirector from "../container/PageDirector";
import { useGlobal } from "../global/user";
import MediaUploader from "../components/MediaUploader";


const DashboardConfig = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const { fields, user } = useGlobal();
  const [form] = Form.useForm();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [isEdit, setIsEdit] = useState(false);

  const [showSpaceBlue, setShowSpaceBlue] = useState(() => localStorage.getItem('_sb_demo') === '1');
  const titleClickCount = useRef(0);
  const titleClickTimer = useRef(null);

  const handleTitleClick = () => {
    clearTimeout(titleClickTimer.current);
    titleClickCount.current += 1;
    if (titleClickCount.current >= 5) {
      titleClickCount.current = 0;
      setShowSpaceBlue(prev => {
        const next = !prev;
        localStorage.setItem('_sb_demo', next ? '1' : '0');
        return next;
      });
    } else {
      titleClickTimer.current = setTimeout(() => { titleClickCount.current = 0; }, 2000);
    }
  };

  // 每個參數 slot 各自的機具清單與參數清單
  const [slotMachines, setSlotMachines] = useState({});
  const [slotParams, setSlotParams] = useState({});
  // 每個折線圖 slot 各自的機具清單
  const [chartSlotMachines, setChartSlotMachines] = useState({});
  // 每個影音 slot 的攝影機清單
  const [videoSlotMachines, setVideoSlotMachines] = useState({});

  // 權限檢查：只有主管 (class == 3) 可以設定儀表板
  useEffect(() => {
    if (user && user.class != process.env.REACT_APP_CONTROL_CALSS_LV) {
      message.warning("您沒有權限訪問此頁面");
      history.push("/control");
    }
  }, [user, history]);
  
  // 載入現有配置（編輯模式）
  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      if (!isMounted) return;
      setLoading(true);
      try {
        const data = await Dashboard.GetByCompanyId(companyId);
        if (!isMounted) return;
        
        if (data) {
          setIsEdit(true);
          setSelectedTemplate(data.template_type);
          setSelectedTheme(data.theme || 'default');
          setStep(2);
          form.setFieldsValue({
            title: data.title,
            template_type: data.template_type,
            parameters: data.parameters,
            charts: data.charts || [],
            videos: data.videos || [],
          });

          // 預先載入每個 slot 已選的機具/參數清單
          if (data.parameters) {
            const machinesMap = {};
            const paramsMap = {};
            await Promise.all(
              data.parameters.map(async (p, i) => {
                if (p.site_id) {
                  try {
                    machinesMap[i] = await Dashboard.GetFieldMachines(p.site_id);
                  } catch (e) {
                    console.warn("載入機具清單失敗", e);
                  }
                }
                if (p.equipment_id) {
                  try {
                    paramsMap[i] = await Dashboard.GetMachineParameters(p.equipment_id);
                  } catch (e) {
                    console.warn("載入參數清單失敗", e);
                  }
                }
              })
            );
            if (!isMounted) return;
            setSlotMachines(machinesMap);
            setSlotParams(paramsMap);
          }

          // 預先載入每個折線圖 slot 已選的機具清單
          if (data.charts) {
            const chartMachinesMap = {};
            const CHART_SUPPORTED_FUNCTIONS = ['function_7', 'function_10', 'function_11', 'function_14', 'function_15'];
            await Promise.all(
              data.charts.map(async (c, i) => {
                if (c.site_id) {
                  try {
                    const machines = await Dashboard.GetFieldMachines(c.site_id);
                    // 過濾只保留支援折線圖的機具
                    chartMachinesMap[i] = machines.filter(machine => {
                      if (!machine.functions) return false;
                      return Object.keys(machine.functions).some(funcKey => 
                        CHART_SUPPORTED_FUNCTIONS.includes(funcKey)
                      );
                    });
                  } catch (e) {
                    console.warn("載入折線圖機具清單失敗", e);
                  }
                }
              })
            );
            if (!isMounted) return;
            setChartSlotMachines(chartMachinesMap);
          }

          // 預先載入攝影機 slot 的機具清單
          if (data.videos) {
            const videoMachinesMap = {};
            const CAMERA_TYPES = [3];
            await Promise.all(
              data.videos.map(async (v, i) => {
                if (v.site_id && v.content_type === 'camera') {
                  try {
                    const machines = await Dashboard.GetFieldMachines(v.site_id);
                    videoMachinesMap[i] = machines.filter(m => CAMERA_TYPES.includes(m.mobileType_id));
                  } catch (e) {
                    console.warn("載入攝影機清單失敗", e);
                  }
                }
              })
            );
            if (!isMounted) return;
            setVideoSlotMachines(videoMachinesMap);
          }
        }
      } catch (error) {
        if (!isMounted) return;
        if (error.response?.status !== 404) {
          message.error("載入儀表板失敗");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [companyId, form]);

  // 選場域 → 清機具/參數、載入機具
  const handleFieldChange = async (fieldId, slotIndex) => {
    const parameters = form.getFieldValue("parameters") || [];
    parameters[slotIndex] = { ...parameters[slotIndex], equipment_id: null, parameter_key: null };
    form.setFieldsValue({ parameters });

    setSlotMachines((prev) => ({ ...prev, [slotIndex]: [] }));
    setSlotParams((prev) => ({ ...prev, [slotIndex]: [] }));

    if (!fieldId) return;
    try {
      const machines = await Dashboard.GetFieldMachines(fieldId);
      setSlotMachines((prev) => ({ ...prev, [slotIndex]: machines }));
    } catch (_) {
      message.error("載入機具清單失敗");
    }
  };

  // 選機具 → 清參數、載入參數
  const handleMachineChange = async (machineId, slotIndex) => {
    const parameters = form.getFieldValue("parameters") || [];
    parameters[slotIndex] = { ...parameters[slotIndex], parameter_key: null };
    form.setFieldsValue({ parameters });

    setSlotParams((prev) => ({ ...prev, [slotIndex]: [] }));

    if (!machineId) return;
    try {
      const params = await Dashboard.GetMachineParameters(machineId);
      setSlotParams((prev) => ({ ...prev, [slotIndex]: params }));
    } catch (_) {
      message.error("載入參數清單失敗");
    }
  };

  // 折線圖：選場域 → 清機具、載入機具（只顯示支援折線圖的機具）
  const CHART_FUNCTIONS = ['function_7', 'function_10', 'function_11', 'function_14', 'function_15'];
  const POSITION_FUNCTIONS = ['function_4'];

  const CAMERA_MOBILE_TYPES = [3];

  const handleVideoFieldChange = async (fieldId, slotIndex) => {
    const videos = form.getFieldValue("videos") || [];
    videos[slotIndex] = { ...videos[slotIndex], camera_id: null };
    form.setFieldsValue({ videos });

    setVideoSlotMachines((prev) => ({ ...prev, [slotIndex]: [] }));

    if (!fieldId) return;
    try {
      const machines = await Dashboard.GetFieldMachines(fieldId);
      const cameras = machines.filter(m => CAMERA_MOBILE_TYPES.includes(m.mobileType_id));
      setVideoSlotMachines((prev) => ({ ...prev, [slotIndex]: cameras }));
      if (cameras.length === 0) message.warning("此場域沒有影像機具（一般鏡頭或紅外線鏡頭）");
    } catch (_) {
      message.error("載入攝影機清單失敗");
    }
  };

  const handleChartFieldChange = async (fieldId, slotIndex, chartType) => {
    const charts = form.getFieldValue("charts") || [];
    const resolvedChartType = chartType ?? charts[slotIndex]?.chart_type ?? 'line_chart';
    charts[slotIndex] = { ...charts[slotIndex], equipment_id: null };
    form.setFieldsValue({ charts });

    setChartSlotMachines((prev) => ({ ...prev, [slotIndex]: [] }));

    if (!fieldId) return;
    try {
      const machines = await Dashboard.GetFieldMachines(fieldId);
      const isLivePosition = resolvedChartType === 'live_position';
      const allowedFunctions = isLivePosition ? POSITION_FUNCTIONS : CHART_FUNCTIONS;

      const filtered = machines.filter(machine => {
        if (!machine.functions) return false;
        return Object.keys(machine.functions).some(f => allowedFunctions.includes(f));
      });
      setChartSlotMachines((prev) => ({ ...prev, [slotIndex]: filtered }));

      if (filtered.length === 0) {
        message.warning(isLivePosition
          ? "此場域沒有已設定室內定位的機具（需要 function_4）"
          : "此場域沒有支援折線圖的機具（需要氣體監測、數位油壓或水情監測）");
      }
    } catch (_) {
      message.error("載入機具清單失敗");
    }
  };

  // 模板配置
  const templates = [
    {
      type: "8P_2L",
      title: "模板一",
      description: "8個參數 + 2個折線圖",
      chartsCount: 2,
      videosCount: 0,
      disabled: false,
    },
    {
      type: "8P_1L_1V",
      title: "模板二",
      description: "8個參數 + 1個折線圖 + 1個媒體卡片",
      chartsCount: 1,
      videosCount: 1,
      disabled: false,
    },
    {
      type: "8P_2V",
      title: "模板三",
      description: "8個參數 + 2個媒體卡片",
      chartsCount: 0,
      videosCount: 2,
      disabled: false,
    },
  ];

  const handleSelectTemplate = (templateType) => {
    setSelectedTemplate(templateType);
    form.setFieldValue("template_type", templateType);
  };

  const handleNextStep = () => {
    if (!form.getFieldValue("title")) {
      message.warning("請輸入儀表板標題");
      return;
    }
    if (!selectedTemplate) {
      message.warning("請選擇模板類型");
      return;
    }
    const template = templates.find((t) => t.type === selectedTemplate);
    form.setFieldsValue({
      parameters: Array(8).fill(null).map((_, i) => ({
        slot_number: i + 1,
        display_name: `參數 ${i + 1}`,
        site_id: null,
        equipment_id: null,
        parameter_key: null,
      })),
      charts: Array(template.chartsCount).fill(null).map((_, i) => ({
        slot_number: i + 1,
        title: `折線圖 ${i + 1}`,
        chart_type: 'line_chart',
        chart_id: null,
        site_id: null,
        equipment_id: null,
      })),
      videos: Array(template.videosCount).fill(null).map((_, i) => ({
        slot_number: i + 1,
        title: `媒體內容 ${i + 1}`,
        content_type: 'image',
        site_id: null,
        camera_id: null,
        media_urls: null,
        carousel_interval: 5,
      })),
    });
    setStep(2);
  };

  const handlePrevStep = () => setStep(1);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      values.theme = selectedTheme === 'default' ? null : selectedTheme;
      setLoading(true);
      if (isEdit) {
        await Dashboard.Update(companyId, values);
        message.success("儀表板更新成功");
      } else {
        await Dashboard.Create(companyId, values);
        message.success("儀表板建立成功");
        setIsEdit(true);
      }
      history.push(`/dashboard/view/${companyId}`);
    } catch (error) {
      if (error.errorFields) {
        message.error("請填寫所有必填欄位");
      } else {
        message.error(error.response?.data?.error || "儲存失敗");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 1: 選擇模板
  const renderStep1 = () => {
    const sb = selectedTheme === 'space_blue';
    const T = sb ? {
      wrap:   { background: 'linear-gradient(135deg, #060d1a, #0a1628)', border: '1px solid rgba(0,160,255,0.3)', borderRadius: 4, padding: 8 },
      param:  { backgroundColor: 'rgba(0,140,255,0.25)', border: '1px solid rgba(0,160,255,0.3)', color: '#7dd3fc' },
      chart:  { backgroundColor: 'rgba(0,140,255,0.15)', border: '1px solid rgba(0,160,255,0.25)', color: '#7dd3fc' },
      video:  { backgroundColor: 'rgba(0,100,200,0.2)',  border: '1px solid rgba(0,160,255,0.25)', color: '#7dd3fc' },
    } : {
      wrap:   { border: '1px solid #d9d9d9', borderRadius: 4, padding: 8, backgroundColor: '#fafafa' },
      param:  { backgroundColor: '#1890ff', color: 'white' },
      chart:  { backgroundColor: '#52c41a', color: 'white' },
      video:  { backgroundColor: '#fa8c16', color: 'white' },
    };
    const tileBase = { borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 };

    return (
      <div>
        <h2 onClick={handleTitleClick} style={{ userSelect: "none" }}>選擇儀表板模板</h2>
        <p style={{ color: "#666", marginBottom: 24 }}>
          請選擇一個模板作為您的儀表板佈局（佈局固定，不可拖拉調整）
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {templates.map((template) => (
            <Card
              key={template.type}
              hoverable={!template.disabled}
              onClick={() => !template.disabled && handleSelectTemplate(template.type)}
              style={{
                border: selectedTemplate === template.type ? "2px solid #1890ff" : "1px solid #d9d9d9",
                cursor: template.disabled ? "not-allowed" : "pointer",
                backgroundColor: template.disabled ? "#f5f5f5" : (selectedTemplate === template.type ? "#e6f7ff" : "white"),
                opacity: template.disabled ? 0.6 : 1,
              }}
            >
              <h3 style={{ marginBottom: 8, color: template.disabled ? "#999" : "inherit" }}>
                {template.title}
                {template.disabled && <span style={{ fontSize: 12, marginLeft: 8, color: "#ff4d4f" }}>（暫不開放）</span>}
              </h3>
              <p style={{ color: template.disabled ? "#999" : "#666", marginBottom: 16 }}>{template.description}</p>
              {/* 佈局預覽 — 顏色隨 theme 切換 */}
              <div style={T.wrap}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginBottom: 4 }}>
                  {[1,2,3,4,5,6,7,8].map((i) => (
                    <div key={i} style={{ ...tileBase, height: 28, ...T.param }}>P{i}</div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4 }}>
                  {template.chartsCount >= 1 && <div style={{ ...tileBase, height: 36, ...T.chart }}>折線1</div>}
                  {template.chartsCount === 2 && <div style={{ ...tileBase, height: 36, ...T.chart }}>折線2</div>}
                  {template.videosCount >= 1 && <div style={{ ...tileBase, height: 36, ...T.video }}>影像1</div>}
                  {template.videosCount === 2 && <div style={{ ...tileBase, height: 36, ...T.video }}>影像2</div>}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* 主題選擇 */}
        <div style={{ marginTop: 20, padding: '14px 18px', border: '1px solid #d9d9d9', borderRadius: 8, background: sb ? 'linear-gradient(135deg,#060d1a,#0a1628)' : '#fafafa' }}>
          <div style={{ marginBottom: 10, fontWeight: 500, color: sb ? '#7dd3fc' : 'inherit' }}>儀表板主題</div>
          <Radio.Group value={selectedTheme} onChange={(e) => setSelectedTheme(e.target.value)}>
            <Radio value="default" style={{ color: sb ? '#c6e4f7' : 'inherit' }}>預設</Radio>
            <Radio value="space_blue" style={{ color: sb ? '#7dd3fc' : 'inherit' }}>太空藍</Radio>
          </Radio.Group>
        </div>

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <Button type="primary" size="large" onClick={handleNextStep}>
            下一步：配置內容
          </Button>
        </div>
      </div>
    );
  };

  // Step 2: 配置內容
  const renderStep2 = () => {
    const template = templates.find((t) => t.type === selectedTemplate);
    return (
      <div>
        <h2>配置儀表板內容</h2>
        <p style={{ color: "#666", marginBottom: 24 }}>設定每個項目要顯示的數值和資料來源</p>

        <Form form={form} layout="vertical">
          {/* 參數設定 */}
          <Collapse
            defaultActiveKey={["parameters"]}
            style={{ marginBottom: 16 }}
            items={[{
              key: "parameters",
              label: "參數設定 (8個)",
              children: (
                <Form.List name="parameters">
                  {(formFields) => (
                    <Row gutter={[16, 16]}>
                      {formFields.map((formField, index) => (
                        <Col key={formField.key} xs={24} sm={24} md={12} lg={12}>
                          <Card type="inner" title={`參數 ${index + 1}`} style={{ height: "100%" }}>
                            <Form.Item
                              label="顯示名稱"
                              name={[formField.name, "display_name"]}
                              rules={[{ required: true, message: "請輸入顯示名稱" }]}
                            >
                              <Input placeholder="例如：油壓" />
                            </Form.Item>

                            <Form.Item label="場域" name={[formField.name, "site_id"]}>
                              <Select
                                placeholder="請選擇場域"
                                allowClear
                                onChange={(val) => handleFieldChange(val, index)}
                              >
                                {fields.map((f) => (
                                  <Select.Option key={f.field_id} value={f.field_id}>
                                    {f.field_name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item label="機具" name={[formField.name, "equipment_id"]}>
                              <Select
                                placeholder="請先選擇場域"
                                allowClear
                                disabled={!slotMachines[index]?.length}
                                onChange={(val) => handleMachineChange(val, index)}
                              >
                                {(slotMachines[index] || []).map((m) => (
                                  <Select.Option key={m.id} value={m.id}>
                                    {m.mobile_name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item label="參數" name={[formField.name, "parameter_key"]}>
                              <Select
                                placeholder="請先選擇機具"
                                allowClear
                                disabled={!slotParams[index]?.length}
                              >
                                {(slotParams[index] || []).map((p) => (
                                  <Select.Option key={p.key} value={p.key}>
                                    {p.label}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item name={[formField.name, "slot_number"]} hidden initialValue={index + 1}>
                              <Input />
                            </Form.Item>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Form.List>
              ),
            }]}
          />

          {/* 折線圖設定 */}
          {template.chartsCount > 0 && (
            <Collapse
              defaultActiveKey={["charts"]}
              style={{ marginBottom: 16 }}
              items={[{
                key: "charts",
                label: `折線圖設定 (${template.chartsCount}個)`,
                children: (
                  <Form.List name="charts">
                    {(formFields) => (
                      <>
                        {formFields.map((formField, index) => {
                          const currentChart = form.getFieldValue(["charts", index]) || {};
                          const chartType = currentChart.chart_type || 'line_chart';
                          const isLivePosition = chartType === 'live_position';

                          return (
                          <Card key={formField.key} type="inner" title={`圖表 ${index + 1}`} style={{ marginBottom: 16 }}>
                            <Form.Item
                              label="圖表標題"
                              name={[formField.name, "title"]}
                              rules={[{ required: true, message: "請輸入圖表標題" }]}
                            >
                              <Input placeholder="例如：油壓趨勢圖" />
                            </Form.Item>

                            <Form.Item label="圖表類型" name={[formField.name, "chart_type"]} initialValue="line_chart">
                              <Radio.Group
                                onChange={(e) => {
                                  const newChartType = e.target.value;
                                  const charts = form.getFieldValue("charts") || [];
                                  const currentSiteId = charts[index]?.site_id;
                                  charts[index] = {
                                    ...charts[index],
                                    chart_type: newChartType,
                                    equipment_id: null,
                                  };
                                  form.setFieldsValue({ charts });
                                  // 若已選場域，依新類型重新載入機具清單
                                  if (currentSiteId) {
                                    handleChartFieldChange(currentSiteId, index, newChartType);
                                  }
                                }}
                              >
                                <Radio value="line_chart">折線圖</Radio>
                                <Radio value="live_position">即時位置追蹤</Radio>
                              </Radio.Group>
                            </Form.Item>

                            <Form.Item label="場域" name={[formField.name, "site_id"]}>
                              <Select
                                placeholder="請選擇場域"
                                allowClear
                                onChange={(val) => handleChartFieldChange(val, index, chartType)}
                              >
                                {fields.map((f) => (
                                  <Select.Option key={f.field_id} value={f.field_id}>
                                    {f.field_name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item label={isLivePosition ? "定位機具" : "機具"} name={[formField.name, "equipment_id"]}>
                              <Select
                                placeholder="請先選擇場域"
                                allowClear
                                disabled={!chartSlotMachines[index]?.length}
                              >
                                {(chartSlotMachines[index] || []).map((m) => (
                                  <Select.Option key={m.id} value={m.id}>
                                    {m.mobile_name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>

                            <Form.Item name={[formField.name, "slot_number"]} hidden initialValue={index + 1}>
                              <Input />
                            </Form.Item>
                          </Card>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                ),
              }]}
            />
          )}

          {/* 媒體內容設定（影像/圖片/影片）*/}
          {template.videosCount > 0 && (
            <Collapse
              defaultActiveKey={["videos"]}
              style={{ marginBottom: 16 }}
              items={[{
                key: "videos",
                label: `媒體內容設定 (${template.videosCount}個)`,
                children: (
                  <Form.List name="videos">
                    {(formFields) => (
                      <>
                        {formFields.map((formField, index) => {
                          const currentVideo = form.getFieldValue(["videos", index]) || {};
                          const contentType = currentVideo.content_type || 'camera';
                          
                          return (
                            <Card key={formField.key} type="inner" title={`媒體內容 ${index + 1}`} style={{ marginBottom: 16 }}>
                              <Form.Item
                                label="內容標題"
                                name={[formField.name, "title"]}
                                rules={[{ required: true, message: "請輸入標題" }]}
                              >
                                <Input placeholder="例如：廠區即時影像" />
                              </Form.Item>

                              <Form.Item
                                label="內容類型"
                                name={[formField.name, "content_type"]}
                                initialValue="image"
                                rules={[{ required: true }]}
                              >
                                <Radio.Group
                                  onChange={(e) => {
                                    const videos = form.getFieldValue("videos") || [];
                                    videos[index] = {
                                      ...videos[index],
                                      content_type: e.target.value,
                                      camera_id: null,
                                      media_urls: null
                                    };
                                    form.setFieldsValue({ videos });
                                  }}
                                >
                                  <Radio value="camera">攝影機串流</Radio>
                                  <Radio value="image">靜態圖片</Radio>
                                  <Radio value="carousel">圖片輪播</Radio>
                                  <Radio value="slideshow">PDF 投影片</Radio>
                                  <Radio value="video">影片播放</Radio>
                                </Radio.Group>
                              </Form.Item>

                              {/* 攝影機串流 */}
                              {contentType === 'camera' && (
                                <>
                                  <Form.Item label="場域" name={[formField.name, "site_id"]}>
                                    <Select
                                      placeholder="選擇場域"
                                      allowClear
                                      onChange={(fieldId) => handleVideoFieldChange(fieldId, index)}
                                    >
                                      {fields.map((f) => (
                                        <Select.Option key={f.field_id} value={f.field_id}>
                                          {f.field_name}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                  <Form.Item label="攝影機" name={[formField.name, "camera_id"]}>
                                    <Select
                                      placeholder="選擇攝影機機具"
                                      allowClear
                                      disabled={!videoSlotMachines[index]?.length}
                                    >
                                      {(videoSlotMachines[index] || []).map((m) => (
                                        <Select.Option key={m.id} value={m.id}>
                                          {m.mobile_name}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </>
                              )}

                              {/* 靜態圖片 */}
                              {contentType === 'image' && (
                                <Form.Item label="上傳圖片" name={[formField.name, "media_urls"]}>
                                  <MediaUploader
                                    maxCount={1}
                                    accept="image/*"
                                    mediaType="image"
                                    value={currentVideo.media_urls}
                                    onChange={(urls) => {
                                      const videos = form.getFieldValue("videos") || [];
                                      videos[index] = { ...videos[index], media_urls: urls };
                                      form.setFieldsValue({ videos });
                                    }}
                                  />
                                </Form.Item>
                              )}

                              {/* 圖片輪播 */}
                              {contentType === 'carousel' && (
                                <>
                                  <Form.Item label="上傳圖片（多張）" name={[formField.name, "media_urls"]}>
                                    <MediaUploader
                                      maxCount={10}
                                      accept="image/*"
                                      mediaType="image"
                                      value={currentVideo.media_urls}
                                      onChange={(urls) => {
                                        const videos = form.getFieldValue("videos") || [];
                                        videos[index] = { ...videos[index], media_urls: urls };
                                        form.setFieldsValue({ videos });
                                      }}
                                    />
                                  </Form.Item>
                                  <Form.Item label="輪播間隔（秒）" name={[formField.name, "carousel_interval"]} initialValue={5}>
                                    <Input type="number" min={1} max={60} placeholder="5" />
                                  </Form.Item>
                                </>
                              )}

                              {/* PDF 投影片 */}
                              {contentType === 'slideshow' && (
                                <>
                                  <Form.Item label="上傳 PDF" name={[formField.name, "media_urls"]}>
                                    <MediaUploader
                                      maxCount={1}
                                      accept=".pdf"
                                      mediaType="slideshow"
                                      value={currentVideo.media_urls}
                                      onChange={(urls) => {
                                        const videos = form.getFieldValue("videos") || [];
                                        videos[index] = { ...videos[index], media_urls: urls };
                                        form.setFieldsValue({ videos });
                                      }}
                                    />
                                  </Form.Item>
                                  <Form.Item label="自動換頁間隔（秒，0 表示不自動換頁）" name={[formField.name, "carousel_interval"]} initialValue={0}>
                                    <Input type="number" min={0} max={300} placeholder="0" />
                                  </Form.Item>
                                </>
                              )}

                              {/* 影片播放 */}
                              {contentType === 'video' && (
                                <Form.Item label="上傳影片" name={[formField.name, "media_urls"]}>
                                  <MediaUploader
                                    maxCount={1}
                                    accept="video/*"
                                    mediaType="video"
                                    value={currentVideo.media_urls}
                                    onChange={(urls) => {
                                      const videos = form.getFieldValue("videos") || [];
                                      videos[index] = { ...videos[index], media_urls: urls };
                                      form.setFieldsValue({ videos });
                                    }}
                                  />
                                </Form.Item>
                              )}

                              <Form.Item name={[formField.name, "slot_number"]} hidden initialValue={index + 1}>
                                <Input />
                              </Form.Item>
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                ),
              }]}
            />
          )}
        </Form>

        <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
          <Button size="large" onClick={handlePrevStep}>上一步</Button>
          <Button type="primary" size="large" onClick={handleSave} loading={loading}>
            儲存並前往儀表板
          </Button>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <CustomHeader />
      <div className="container-fluid">
        <div className="page-container fade-in">
          <PageDirector />
          <div style={{ marginTop: 40 }}></div>
          <Card title="儀表板編輯器">
            <Spin spinning={loading}>
              <Form form={form} layout="vertical">
                <Form.Item
                  label="儀表板標題"
                  name="title"
                  rules={[{ required: true, message: "請輸入標題" }]}
                >
                  <Input placeholder="例如：XX公司油壓監控" size="large" />
                </Form.Item>
                <Form.Item name="template_type" hidden>
                  <Input />
                </Form.Item>
              </Form>

              <Steps current={step - 1} style={{ marginBottom: 32 }}>
                <Steps.Step title="選擇模板" />
                <Steps.Step title="配置內容" />
              </Steps>

              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
            </Spin>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DashboardConfig;
